export const useShopperContext = () => {
  const SHOPPER_CONTEXT_CACHE_KEY = 'shopper_context'
  const SHOPPER_CONTEXT_CACHE_TTL_MS = 6 * 60 * 60 * 1000 // 6 hours localStorage lifetime, SFCC default shopperContext duration is 12 hours, so this should be safe to assume context is still valid. Adjust as needed.

  const shopper = useShopper()

  type ShopperContextCache<T> = {
    context: T
    created: number
  }

  type ShopperContext = {
    customerGroupIds?: string[]
    effectiveDateTime?: string
    [key: string]: unknown
  }

  const getCachedContext = <T>(): T | null => {
    try {
      const raw = localStorage.getItem(SHOPPER_CONTEXT_CACHE_KEY)
      if (!raw) return null

      const parsed = JSON.parse(raw) as Partial<ShopperContextCache<T>>
      if (!parsed.context || !parsed.created || !Number.isFinite(parsed.created)) return null
      if (Date.now() - parsed.created > SHOPPER_CONTEXT_CACHE_TTL_MS) return null

      return parsed.context
    } catch {
      return null
    }
  }

  const setCachedContext = <T>(context: T): void => {
    try {
      localStorage.setItem(
        SHOPPER_CONTEXT_CACHE_KEY,
        JSON.stringify({ context, created: Date.now() } satisfies ShopperContextCache<T>),
      )
    } catch {
      // ignore cache write failures
    }
  }

  const getStatusCode = (error: unknown): number | undefined => {
    return (
      (error as { status?: number; response?: { status?: number } }).status ??
      (error as { response?: { status?: number } }).response?.status
    )
  }

  const parseCustomerGroupIds = (customerGroup: string | undefined): string[] => {
    return (
      customerGroup
        ?.split(',')
        .map((id) => id.trim())
        .filter(Boolean) || []
    )
  }

  // SFCC treats an absent key and an empty value differently, so only set what we have.
  const buildContextBody = (siteDate: string | undefined, customerGroupIds: string[]) => {
    const body: ShopperContext = {}
    if (siteDate) {
      body.effectiveDateTime = siteDate
    }
    if (customerGroupIds.length) {
      body.customerGroupIds = customerGroupIds
    }
    return body
  }

  const getExistingContext = async (headers: Record<string, string>, usid: string) => {
    try {
      const cachedContext = getCachedContext<unknown>()
      if (cachedContext) {
        return { context: cachedContext, statusCode: 200 }
      }

      const response = await shopper.shopperContexts.getShopperContext({
        headers,
        parameters: { usid, organizationId: shopper.config.organizationId, siteId: shopper.config.siteId },
      })

      setCachedContext(response)
      return { context: response, statusCode: 200 }
    } catch (error) {
      return { context: null, statusCode: getStatusCode(error) }
    }
  }

  const normalizeIsoDate = (value: string | undefined): string | undefined => {
    if (!value) return undefined

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    // toISOString() => yyyy-mm-ddTHH:mm:ss.sssZ, remove milliseconds
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
  }

  const createOrUpdateContext = async (siteDateQuery: unknown, customerGroupQuery: unknown) => {
    const siteDate = normalizeIsoDate(queryString(siteDateQuery))
    const customerGroupIds = parseCustomerGroupIds(queryString(customerGroupQuery))

    if (!siteDate && !customerGroupIds.length) return

    try {
      const headers = await shopper.getAuthHeaders()
      const token = headers.Authorization?.split(' ')[1]

      if (!token) return

      const { usId: usid } = getIdsFromToken(token)

      if (!usid) return

      const existingContext = await getExistingContext(headers, usid)

      // 404: no shopper context exists yet; 412: precondition failed (context expired/invalid) — both require creation
      if (existingContext.statusCode === 404 || existingContext.statusCode === 412) {
        await shopper.shopperContexts.createShopperContext({
          headers,
          parameters: { usid },
          body: buildContextBody(siteDate, customerGroupIds),
        })
        setCachedContext({ customerGroupIds, effectiveDateTime: siteDate })
        return
      }

      if (existingContext.statusCode !== 200 || !existingContext.context) return

      const context = existingContext.context as ShopperContext
      const existingCustomerGroupIds = context.customerGroupIds || []
      const hasExactCustomerGroupMatch =
        customerGroupIds.length === existingCustomerGroupIds.length &&
        customerGroupIds.every((groupId) => existingCustomerGroupIds.includes(groupId))
      const hasExactSiteDateMatch = (context.effectiveDateTime || '') === (siteDate || '')

      if (hasExactCustomerGroupMatch && hasExactSiteDateMatch) return

      await shopper.shopperContexts.updateShopperContext({
        headers,
        parameters: { usid },
        body: buildContextBody(siteDate, customerGroupIds),
      })
      setCachedContext({ customerGroupIds, effectiveDateTime: siteDate })
    } catch (error) {
      console.error('Error creating shopper context', error)
    }
  }

  return {
    createOrUpdateContext,
  }
}
